import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-mono text-base text-white uppercase tracking-wider mb-4 border-b border-[#2A2A2A] pb-3">
        {title}
      </h2>
      <div className="text-[#BEB3A6] font-sans leading-relaxed space-y-3 text-[0.95rem]">
        {children}
      </div>
    </section>
  );
}

export default function TermsPage() {
  return (
    <div className="bg-[#111111] min-h-screen">
      <Helmet>
        <title>Terms &amp; Conditions – Restricted Deals</title>
        <meta name="description" content="Terms and conditions for using Restricted Deals and subscribing to our travel deals newsletter." />
        <link rel="canonical" href="https://restricteddeals.com/terms" />
      </Helmet>
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="eyebrow eyebrow-red mb-4">// Legal</p>
        <h1 className="font-mono text-4xl md:text-5xl text-white uppercase tracking-tight mb-4">
          Terms &amp; Conditions
        </h1>
        <p className="text-[#555] font-mono text-xs tracking-widest uppercase mb-14">
          Last updated: April 2026 · Restricted Deals · Netherlands
        </p>

        <Section title="1. About">
          <p>
            These Terms govern your use of Restricted Deals (the "Service") at{" "}
            <strong className="text-white">restricteddeals.com</strong>, operated from the
            Netherlands. By visiting the site or subscribing to the newsletter you agree to these Terms.
          </p>
        </Section>

        <Section title="2. The service">
          <p>
            Restricted Deals is a curated travel deals newsletter covering flights, hotels, cruises,
            and packages. We identify below-average prices and publish them on our website and by
            email. The Service is free to use.
          </p>
        </Section>

        <Section title="3. No guarantee of price or availability">
          <p>
            Travel prices are extremely volatile. Prices shown on this site are indicative at the time
            of discovery and may have changed or expired by the time you check.{" "}
            <strong className="text-white">
              Always verify the current price directly with the airline, hotel, cruise line, or
              booking platform before making any purchase.
            </strong>
          </p>
          <p>
            We do not guarantee that any deal will be available when you click through. We accept no
            liability for prices that are no longer available at booking.
          </p>
        </Section>

        <Section title="4. Affiliate links & commission disclosure">
          <p>
            This site contains affiliate links to third-party platforms including airline websites,
            Booking.com, and Trip.com. When you click an affiliate link and complete a purchase we may
            receive a commission from the third party at no extra cost to you.
          </p>
          <p>
            Affiliate relationships have <strong className="text-white">no influence</strong> on which
            deals we feature or how we rank them. We only publish deals we consider genuinely good value.
          </p>
          <p>
            We are not a party to any contract between you and the booking platform. Cancellations,
            refunds, and disputes must be resolved directly with the airline or platform.
          </p>
        </Section>

        <Section title="5. Newsletter subscription">
          <p>
            By subscribing you consent to receive deal emails from us. We operate a double opt-in
            process: your address will not receive deal emails until you confirm via the link we send.
            You can unsubscribe at any time with one click on the link at the bottom of every email.
          </p>
        </Section>

        <Section title="6. Intellectual property">
          <p>
            All content on the site — including text, design, and the Restricted Deals logo — is our
            property. You may not reproduce or redistribute it without our written permission.
          </p>
        </Section>

        <Section title="7. Limitation of liability">
          <p>To the maximum extent permitted by Dutch law, Restricted Deals is not liable for:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Any loss arising from reliance on deal information published on the site.</li>
            <li>Indirect or consequential losses, including missed flights or wasted travel costs.</li>
            <li>Actions or omissions of third-party booking platforms.</li>
          </ul>
        </Section>

        <Section title="8. Governing law">
          <p>
            These Terms are governed by Dutch law. Any disputes shall be submitted to the competent
            court in the Netherlands.
          </p>
        </Section>

        <Section title="9. Changes">
          <p>
            We may update these Terms at any time by posting a revised version on this page.
            Continued use of the Service after an update constitutes acceptance of the revised Terms.
          </p>
        </Section>

        <div className="hr-dot my-10" />
        <div className="flex gap-6 text-[11px] font-mono tracking-widest uppercase text-[#555]">
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
